import { Button } from "antd";
import styled from "styled-components";

export const RootContainer = styled.div`
  .ant-modal-content {
    width: 100%;
    height: 100%;
  }

  .data-grid-container {
    width: 100%;
  }
  .ant-modal-body {
    max-height: 800px;
    padding-bottom: 24px;
  }
  .gridWrapper > :first-child {
    width: 800px;
    height: 800px;
  }
`;

export const ContentContainer = styled.div`
  padding: 0 20px;
`;

export const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-height: 650px;
  overflow-y: auto;
`;

export const ErrorMessage = styled.div`
  color: red;
  margin: 0 0 20px;
  padding: 10px;
  border: 1px solid red;
  border-radius: 4px;
  background-color: #fff2f0;
`;

export const ProductMappingContainer = styled.div`
  border: 1px solid #d9e1e2;
  border-radius: var(--border-radius);
  margin: 0 auto 16px;
  width: 100%;
  max-width: 700px;
`;

// Grid components
export const ProductMappingHeader = styled.div<{ showPrimary?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.showPrimary ? "52px 1fr minmax(130px, 1fr) 32px" : "1fr 1fr 32px"};
  padding: 12px 16px;
  background-color: #f5f7f7;
  border-bottom: 1px solid #d9e1e2;
  font-weight: 500;
  gap: 32px;
`;

export const ProductMappingRowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

export const ProductMappingRow = styled.div<{ showPrimary?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.showPrimary ? "52px 1fr minmax(130px, 1fr) 32px" : "1fr 1fr 32px"};
  padding: 12px 16px;
  background-color: white;
  border-left: none;
  border-bottom: 1px solid #d9e1e2;
  align-items: center;
  gap: 32px;

  &:last-child {
    border-bottom: none;
  }
`;

export const ErrorWrapper = styled.div`
  display: flex;
  color: red;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const WarningWrapper = styled.div`
  display: flex;
  color: orange;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const MatchedWrapper = styled.div`
  display: flex;
  color: green;
  align-items: center;
  justify-content: center;
`;

// Form elements
export const ProductDropdownWrapper = styled.div`
  width: 100%;

  .ant-select {
    width: 100% !important;
  }

  .ant-select-selector {
    width: 100% !important;
  }
`;

export const DeleteButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-btn {
    border: none;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const ActionButton = styled(Button)`
  color: ${() => "#a2aaad"};
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;

  .MuiSvgIcon-root {
    font-size: 18px;
  }

  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
    background: transparent !important;
    border: none !important;
  }
  &:focus,
  &:active {
    color: ${() => "#a2aaad"};
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
  &::after {
    display: none !important;
  }
`;

// Text elements
export const Title = styled.div`
  font-weight: 500;
`;

export const ProductName = styled.div`
  font-weight: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NoProductsFound = styled.div`
  color: #a2aaad;
  font-style: italic;
  padding: 16px 0;
  text-align: center;
  width: 80%;
  margin: 0 auto;
`;

// Legend components
export const Instructions = styled.div`
  margin-top: 12px;
  font-size: var(--fontSize);
  font-weight: normal;
`;

export const ErrorInstructions = styled.div`
  font-style: normal;
  font-weight: normal;
  color: var(--color-text);
`;

/**
 * Button styled for the GroupBy mapping selector in well data upload.
 * Displays selected field name with an arrow icon.
 */
export const StyledMappingButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  height: 32px;
  text-align: left;
  padding: 4px 11px;
  border: 1px solid #d9d9d9;
  border-radius: var(--border-radius);
  background: white;

  & > span {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: var(--ant-primary-color);
    color: var(--ant-primary-color);
  }

  &:focus {
    border-color: var(--ant-primary-color);
    box-shadow: 0 0 0 2px rgba(var(--ant-primary-color-rgb), 0.2);
  }
`;

/**
 * Primary product toggle button - shows "P" icon.
 * Green/filled when primary, muted when not.
 */
export const PrimaryProductButton = styled.button<{ isPrimary: boolean }>`
  font-weight: bold;
  border-radius: var(--border-radius);
  cursor: ${(props) => (props.isPrimary ? "default" : "pointer")};
  padding: 3px 6px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.2s var(--ease);
  font-size: 12px;

  color: ${(props) => (props.isPrimary ? "white" : "var(--color-text-50)")};
  background: ${(props) => (props.isPrimary ? "var(--color-primary)" : "transparent")};
  border: 1px solid
    ${(props) => (props.isPrimary ? "var(--color-primary)" : "var(--color-text-30)")};

  &:hover:not(:disabled) {
    color: ${(props) => (props.isPrimary ? "white" : "var(--color-primary)")};
    border-color: var(--color-primary);
  }

  &:disabled {
    cursor: default;
  }
`;

export const PrimaryColumnWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
